import { Outlet } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import { Navigation } from './Navigation.tsx';
import { createContext, useContext, useState } from 'react';
import { RealtimeProvider } from '../contexts/RealtimeProvider.js';
import { SubscriptionProvider } from '../contexts/SubscriptionProvider';

type LayoutContextProps = {
  showToast: (val: string) => void;
  renderToast: ({
    message,
    type,
  }: {
    message: string;
    type: 'success' | 'error' | 'warning';
  }) => void;
};

export const LayoutContext = createContext<LayoutContextProps>({
  showToast: () => {},
  renderToast: () => {},
});

export const useLayoutContext = () => useContext(LayoutContext);

function Layout() {
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'warning';
  } | null>(null);

  const handleShowToast = (message: string) => {
    setToast({ message, type: 'success' });
    setTimeout(() => {
      setToast(null);
    }, 3000);
  };

  const handleRenderToast = ({
    message,
    type,
  }: {
    message: string;
    type: 'success' | 'error' | 'warning';
  }) => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 3000);
  };

  return (
    <AnimatePresence>
      <LayoutContext.Provider
        value={{
          showToast: handleShowToast,
          renderToast: handleRenderToast,
        }}
      >
        <RealtimeProvider>
          <SubscriptionProvider>
            <div className="flex w-full flex-col-reverse md:h-screen lg:flex-row">
              <Navigation />
              <div className="h-fit w-full overflow-y-scroll md:h-screen">
                <Outlet />
              </div>
              {toast && (
                <div className="toast toast-center toast-top fixed z-[9999] w-80">
                  <div
                    className={`alert ${
                      toast.type === 'error'
                        ? 'alert-error'
                        : toast.type === 'warning'
                          ? 'alert-warning'
                          : 'alert-success'
                    }`}
                  >
                    <span>{toast.message}</span>
                  </div>
                </div>
              )}
            </div>
          </SubscriptionProvider>
        </RealtimeProvider>
      </LayoutContext.Provider>
    </AnimatePresence>
  );
}

export default Layout;
